import { TreeNode } from "@farris/ui-treetable";
import { NewDataType, NewEntitySet, NewEntityType, NewNavigationType, NewSchema } from "../common/NewSchema";
import { EntitySet, EntityType, NavigationType, Schema } from "../common/Schema";
import { VarEntity, InjectedType } from "../common/ExpressEntity";
import { CheckContext } from "./CheckExpress";
import { Type } from "../expression-entity-grid/expression-entity-grid.component";

export class Table {
  tableName: string;
  name: string;
  properties: Property[] = [];
  parent: string
}

class Property {
  code: string;
  name: string;
  type: string;
}

/**
 * 构造变量（上下文和会话）
 * @description 校验上下文构造上下文变量和会话变量
 * @param context 校验上下文
 * @param contexts 上下文变量
 * @param sessions 会话变量
 */
export function buildVariable(context: CheckContext, contexts: VarEntity[], sessions: VarEntity[]) {
  //存储上下文和会话
  contexts.forEach(c => {
    context.contextVariables.push(c.key);
  })
  sessions.forEach(s => {
    context.sessionVariables.push(s.key);
  })
}

/**
 * 构造数据模型（NewSchema）
 * @param context 
 * @param newSchemaMap 
 * @param injectedEntities 
 */
export function buildNewSchemaEntityStruct(context: CheckContext, newSchemaMap: any, injectedEntities: any) {
  let entities = SchemaToStructUtils.onInitNewSchema(newSchemaMap, injectedEntities);
  entities.forEach(entity => {
    getEntityStruct(entity, context.entityStructs);
  })
}

/**
 * 构造数据模型（Schema）
 * @param entityStructs 
 * @param schemaMap 
 * @param injectedEntities 
 */
export function buildSchemaEntityStruct(context: CheckContext, schemaMap: any, injectedEntities: any) {
  let entities = SchemaToStructUtils.OnInitSchema(schemaMap, injectedEntities);
  entities.forEach(entity => {
    getEntityStruct(entity, context.entityStructs);
  })
}

function getEntityStruct(t: TreeNode, tables: Table[]) {
  if (t.children.length != 0) {
    //有属性，建立表
    let table = new Table();
    if (t.data.type.code == Type.EntityType_Main || t.data.type.code == Type.EntityType_Other || t.data.type.code == Type.EntityType_ChildTable) {
      table.tableName = t.data.code;
      table.name = t.data.name;
    } else if (t.data.type.code == Type.EntitySet) {
      table.properties.push({ code: "length", name: "大小", type: "Integer" })  //子表类型，额外存储length属性
      table.tableName = t.data.code;
      table.name = t.data.name;
    }

    //有parent则赋值
    if (t.parent != null) {
      table.parent = t.parent.data.code;
    }

    //存储属性
    t.children.forEach(pro => {
      if (pro.data.type.code == Type.EntityType_Main || pro.data.type.code == Type.EntityType_Other || pro.data.type.code == Type.EntitySet || pro.data.type.code == Type.EntityType_ChildTable) {
        getEntityStruct(pro, tables);
      }
      let ppp: Property = { code: pro.data.code, name: pro.data.name, type: pro.data.type.code }
      if (pro.data.type.code == Type.EntitySet) ppp.type = "Set";
      else if (pro.data.type.code == Type.EntityType_Main || pro.data.type.code == Type.EntityType_Other || pro.data.type.code == Type.EntityType_ChildTable) ppp.type = "Type";

      table.properties.push(ppp);
    })
    tables.push(table);
  }
}

export class SchemaToStructUtils {
  private static oldNumber = 99999;
  private static newNumber = 999;
  //#============NewSchema=================#

  /**
   * @description 初始化NewSchema
   */
  public static onInitNewSchema(newSchemaMap: any, injectedEntities: any, i18nTypes: Map<string, string> = null): TreeNode[] {
    this.newNumber = 999;
    //注入实体存在且有值
    var entities: TreeNode[] = [];
    if (injectedEntities && injectedEntities.length > 0) {
      //初始化navigatorMap1(禁忌列表)
      let navigatorMap1 = new Map<string, InjectedType>();

      //根据注入实体的schemaName找到对应的schema
      injectedEntities.forEach(entity => {
        let schema = newSchemaMap.get(entity.schemaName);
        if (schema != null) {
          let root: TreeNode = { data: {}, children: [] };
          let customSchemaName = entity.schemaName;
          //根据注入实体类型--->执行不同方法
          switch (entity.injectedType) {
            case InjectedType.EntitySet: {
              let set = schema.entitySets.find(set => set.code == entity.injectedName);
              root = this.convertNewEntitySet(set, schema, navigatorMap1, customSchemaName, i18nTypes);
            }; break;
            case InjectedType.EntityType_ChildTable:
            case InjectedType.EntityType: {
              let type = schema.entityTypes.find(type => type.code == entity.injectedName);
              root = this.convertNewEntityType(type, schema, navigatorMap1, customSchemaName, i18nTypes);
            }; break;
          }
          if (root != null) {
            root.data.code = entity.name;
            if (entity.injectedType == InjectedType.EntityType_ChildTable) {
              root.data.type.code = Type.EntityType_ChildTable;
            } else {
              root.data.type.code = Type.EntityType_Main;
            }
            root.data.type.description = this.getTypeDescription(root.data.type.code, i18nTypes);
            entities.push(root);
          }
        }
      });
    }
    return entities;
  }

  /**
   * @description 转换实体类型(EntityType)
   * @param entityType
   * @param schema
   * @param navigatorMap1
   * @returns
   */
  public static convertNewEntityType(type: NewEntityType, schema: NewSchema, navigatorMap1: any, customSchemaName: any, i18nTypes: Map<string, string>): TreeNode {
    if (type) {
      //初始化：NewEntityType的节点
      let treeNode: TreeNode = {
        data: {
          id: this.newNumber++,
          code: type.code,
          name: type.name,
          type: {
            code: Type.EntityType_Other,
            description: this.getTypeDescription(Type.EntityType_Other, i18nTypes)
          },
          schema: customSchemaName,
        },
        children: []
      };

      //pro
      navigatorMap1.set(type.name + type.code, InjectedType.EntityType);
      if (type.properties != null && type.properties.length > 0) {
        type.properties.forEach(pro => {
          let proNode: TreeNode = {
            data: {
              id: this.newNumber++,
              code: pro.code,
              name: pro.name,
              type: {
                code: this.toType(pro.dataType),
                description: this.getTypeDescription(this.toType(pro.dataType), i18nTypes)
              },
              schema: customSchemaName,
            },
            children: []
          };
          proNode.parent = treeNode;
          treeNode.children.push(proNode);
        });
      }

      //naPro
      if (type.navigationProperties != null && type.navigationProperties.length > 0) {
        type.navigationProperties.forEach(naPro => {
          //判断是否导航属性对应的名称在禁忌表中
          if (!(navigatorMap1.get(naPro.navigationName + naPro.code))) {
            if (naPro.navigationType == NewNavigationType.EntityType) {
              let entityType = schema.entityTypes.find(type1 => type1.code == naPro.navigationName);
              let a = this.convertNewEntityType(entityType, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            } else if (naPro.navigationType == NewNavigationType.EntitySet) {
              let entitySet = schema.entitySets.find(set1 => set1.code == naPro.navigationName);
              let a = this.convertNewEntitySet(entitySet, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            } else if (naPro.navigationType == NewNavigationType.EntityEnum) {
              let entityEnum = schema.entityTypes.find(enum1 => enum1.code == naPro.navigationName);
              let a = this.convertNewEntityEnum(entityEnum, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            }
          } else {
            //若导航属性在禁忌表中
            if (naPro.navigationType == NewNavigationType.EntityType) {
              let entityType = schema.entityTypes.find(type1 => type1.code == naPro.navigationName);
              let a = this.convertNewEntityType(entityType, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            } else if (naPro.navigationType == NewNavigationType.EntitySet) {
              let entitySet = schema.entitySets.find(set1 => set1.code == naPro.navigationName);
              let a = this.convertNewEntitySet(entitySet, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            } else if (naPro.navigationType == NewNavigationType.EntityEnum) {
              let entityEnum = schema.entityTypes.find(enum1 => enum1.code == naPro.navigationName);
              let a = this.convertNewEntityEnum(entityEnum, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            }
          }
        });
      }
      return treeNode;
    }
    return null;
  }

  /**
   * @description 转换实体集(EntitySet)
   * @param set
   * @param schema
   * @param navigatorMap1
   * @returns
   */
  public static convertNewEntitySet(set: NewEntitySet, schema: NewSchema, navigatorMap1: any, customSchemaName: any, i18nTypes: Map<string, string>): TreeNode {
    if (set) {
      //将entityset转化为TreeNode结构用于展示
      let treeNode: TreeNode = {
        data: {
          id: this.newNumber++,
          code: set.code,
          name: set.name,
          type: {
            code: Type.EntitySet,
            description: this.getTypeDescription(Type.EntitySet, i18nTypes)
          },
          schema: customSchemaName,
        },
        children: []
      };
      //将转化完成的实体集添加进禁忌表，防止循环转化
      navigatorMap1.set(set.name, InjectedType.EntitySet);
      //找到实体集对应的实体类型
      let entityType = schema.entityTypes.find(entityType => entityType.code == set.entityType);
      //将entitytype转化为TreeNode结构用于展示
      this.convertNewEntityType(entityType, schema, navigatorMap1, customSchemaName, i18nTypes).children.forEach(child => {
        treeNode.children.push(child);
      });
      return treeNode;
    }
    //实体集为空或未定义则返回空
    else return null;
  }

  //解析枚举
  public static convertNewEntityEnum(type: NewEntityType, schema: NewSchema, navigatorMap1: any, customSchemaName: any, i18nTypes: Map<string, string>): TreeNode {
    if (type) {
      //初始化：NewEntityEnum的节点
      let treeNode: TreeNode = {
        data: {
          id: this.newNumber++,
          code: type.code,
          name: type.name,
          type: {
            code: Type.EntityEnum,
            description: this.getTypeDescription(Type.EntityEnum, i18nTypes)
          },
          schema: customSchemaName,
        },
        children: []
      };

      //pro
      navigatorMap1.set(type.name + type.code, InjectedType.EntityEnum);
      if (type.properties != null && type.properties.length > 0) {
        type.properties.forEach(pro => {
          let proNode: TreeNode = {
            data: {
              id: this.newNumber++,
              code: pro.code,
              name: pro.name,
              type: {
                code: "",
                description: ""
              },
              schema: customSchemaName,
            },
            children: []
          };
          //根据枚举值的类型转换
          let dataTypeLowerCase = pro.dataType.toLowerCase();
          if (dataTypeLowerCase == "string") {
            proNode.data.type.code = Type.EntityEnum_String;
            proNode.data.type.description = this.getTypeDescription(Type.EntityEnum_String, i18nTypes);
          } else if (dataTypeLowerCase == "int" || dataTypeLowerCase == "integer" || dataTypeLowerCase == "byte" || dataTypeLowerCase == "decimal") {
            proNode.data.type.code = Type.EntityEnum_Int;
            proNode.data.type.description = this.getTypeDescription(Type.EntityEnum_Int, i18nTypes);
          }
          proNode.parent = treeNode;
          treeNode.children.push(proNode);
        });
      }
      return treeNode;
    }
    return null;
  }

  //Type翻译为国际化资源
  public static getTypeDescription(dataType: any, i18nTypes: Map<string, string>) {
    if (i18nTypes != null) {
      if (dataType != null) {
        switch (dataType) {
          case Type.Boolean:
            return i18nTypes.get(Type.Boolean);
          case Type.Byte:
            return i18nTypes.get(Type.Byte);
          case Type.Integer:
            return i18nTypes.get(Type.Integer);
          case Type.Decimal:
            return i18nTypes.get(Type.Decimal);
          case Type.Date:
            return i18nTypes.get(Type.Date);
          case Type.DateTime:
            return i18nTypes.get(Type.DateTime);
          case Type.String:
            return i18nTypes.get(Type.String);
          case Type.EntityType_Main:
            return i18nTypes.get(Type.EntityType_Main);
          case Type.EntityType_ChildTable:
            return i18nTypes.get(Type.EntityType_ChildTable);
          case Type.EntityType_Other:
            return i18nTypes.get(Type.EntityType_Other);
          case Type.EntitySet:
            return i18nTypes.get(Type.EntitySet);
          case Type.EntityEnum:
            return i18nTypes.get(Type.EntityEnum);
          case Type.EntityEnum_Int:
            return i18nTypes.get(Type.EntityEnum_Int);
          case Type.EntityEnum_String:
            return i18nTypes.get(Type.EntityEnum_String);
          default:
            return dataType;
        }
      }
    } else {
      if (dataType != null) {
        switch (dataType) {
          case Type.Boolean:
            return Type.Boolean;
          case Type.Byte:
            return Type.Byte;
          case Type.Integer:
            return Type.Integer;
          case Type.Decimal:
            return Type.Decimal;
          case Type.Date:
            return Type.Date;
          case Type.DateTime:
            return Type.DateTime;
          case Type.String:
            return Type.String;
          case Type.EntityType_Main:
            return Type.EntityType_Main;
          case Type.EntityType_ChildTable:
            return Type.EntityType_ChildTable;
          case Type.EntityType_Other:
            return Type.EntityType_Other;
          case Type.EntitySet:
            return Type.EntitySet;
          case Type.EntityEnum:
            return Type.EntityEnum;
          case Type.EntityEnum_Int:
            return Type.EntityEnum_Int;
          case Type.EntityEnum_String:
            return Type.EntityEnum_String;
          default:
            return dataType;
        }
      }
    }
  }

  //转化为Type
  public static toType(dataType: any) {
    if (dataType != null) {
      switch (dataType) {
        case NewDataType.Boolean:
          return Type.Boolean;
        case NewDataType.Byte:
          return Type.Byte;
        case NewDataType.Integer:
          return Type.Integer;
        case NewDataType.Decimal:
          return Type.Decimal;
        case NewDataType.Date:
          return Type.Date;
        case NewDataType.DateTime:
          return Type.DateTime;
        case NewDataType.String:
          return Type.String;
        case "Binary":
          return Type.Byte;
        case "Text":
          return Type.String;
        default:
          return dataType;
      }
    }
  }

  //#=============Schema=====================#

  //Schema转换为TreeNode[]
  public static OnInitSchema(schemaMap: any, injectedEntities: any, i18nTypes: Map<string, string> = null): TreeNode[] {
    this.oldNumber = 99999;
    var entities: TreeNode[] = [];
    if (injectedEntities && injectedEntities.length > 0) {
      let navigatorMap = new Map<string, InjectedType>();
      injectedEntities.forEach(entity => {
        let schema = schemaMap.get(entity.schemaName);
        if (schema != null) {
          let root: TreeNode = {
            data: {},
            children: []
          };
          switch (entity.injectedType) {
            case InjectedType.EntitySet: {
              let set = schema.entitySets.find(entitySet => entitySet.name == entity.injectedName);
              root = this.ConvertEntitySet(set, schema, navigatorMap, entity.schemaName, i18nTypes);
            }; break;
            case InjectedType.EntityType_ChildTable:
            case InjectedType.EntityType: {
              let type = schema.entityTypes.find(entityType => entityType.name == entity.injectedName);
              root = this.ConvertEntityType(type, schema, navigatorMap, entity.schemaName, i18nTypes);
            }; break;
          }
          if (root != null) {
            root.data.code = entity.name;
            if (entity.injectedType == InjectedType.EntityType_ChildTable) {
              root.data.type.code = Type.EntityType_ChildTable;
            } else {
              root.data.type.code = Type.EntityType_Main;
            }
            root.data.type.description = this.getTypeDescription(root.data.type.code, i18nTypes);
            entities.push(root);
          }
        }
      });
    }
    return entities;
  }
  //实体类型转化
  public static ConvertEntityType(type: EntityType, schema: Schema, navigatorMap1: any, customSchemaName: any, i18nTypes: Map<string, string>): TreeNode {
    if (type) {
      let treeNode: TreeNode = {
        data: {
          id: this.oldNumber++,
          code: type.name,
          name: type.description,
          type: {
            code: Type.EntityType_Other,
            description: this.getTypeDescription(Type.EntityType_Other, i18nTypes)
          },
          schema: customSchemaName,
        },
        children: []
      };
      //转化的type添加禁忌表
      navigatorMap1.set(type.name + type.id, InjectedType.EntityType);

      //pro转化
      if (type.properties != null && type.properties.length > 0) {
        type.properties.forEach(pro => {
          let proNode: TreeNode = {
            data: {
              id: this.oldNumber++,
              code: pro.name,
              name: pro.description,
              type: {
                code: this.toType(pro.type),
                description: this.getTypeDescription(this.toType(pro.type), i18nTypes)
              },
              schema: customSchemaName,
            },
            children: []
          };
          proNode.parent = treeNode;
          treeNode.children.push(proNode);
        });
      }

      //napro转化
      if (type.navigationProperties != null && type.navigationProperties.length > 0) {
        type.navigationProperties.forEach(naPro => {
          //判断是否导航属性对应的名称不在禁忌表中
          if (!(navigatorMap1.get(naPro.name + naPro.navigationId))) {
            if (naPro.type == NavigationType.EntitySet) {
              //若为实体集
              let set = schema.entitySets.find(entitySet => entitySet.name == naPro.name);
              let a = this.ConvertEntitySet(set, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            } else if (naPro.type == NavigationType.EntityType) {
              //若为实体类型
              let type1 = schema.entityTypes.find(entitytype => entitytype.id == naPro.navigationId && entitytype.name == naPro.name);
              let a = this.ConvertEntityType(type1, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            } else if (naPro.type == NavigationType.EntityEnum) {
              //若为枚举
              let enum1 = schema.entityTypes.find(entitytype => entitytype.id == naPro.navigationId && entitytype.name == naPro.name);
              let a = this.ConvertEntityEnum(enum1, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            }
          } else {
            //若导航属性在禁忌表中
            if (naPro.type == NavigationType.EntitySet) {
              //若为实体集
              let set = schema.entitySets.find(entitySet => entitySet.name == naPro.name);
              let a = this.ConvertEntitySet(set, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            } else if (naPro.type == NavigationType.EntityType) {
              //若为实体类型
              let type1 = schema.entityTypes.find(entitytype => entitytype.id == naPro.navigationId && entitytype.name == naPro.name);
              let a = this.ConvertEntityType(type1, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            } else if (naPro.type == NavigationType.EntityEnum) {
              //若为枚举
              let enum1 = schema.entityTypes.find(entitytype => entitytype.id == naPro.navigationId && entitytype.name == naPro.name);
              let a = this.ConvertEntityEnum(enum1, schema, navigatorMap1, customSchemaName, i18nTypes);
              a.parent = treeNode;
              treeNode.children.push(a);
            }
          }
        });
      }
      return treeNode;
    }
    //若type为空或未定义
    else return null;
  }
  //实体集转化
  public static ConvertEntitySet(set: EntitySet, schema: Schema, navigatorMap1: any, customSchemaName: any, i18nTypes: Map<string, string>): TreeNode {
    if (set) {
      //将set转化为TreeNode结构
      let treeNode: TreeNode = {
        data: {
          id: this.oldNumber++,
          code: set.name,
          name: set.description,
          type: {
            code: Type.EntitySet,
            description: this.getTypeDescription(Type.EntitySet, i18nTypes)
          },
          schema: customSchemaName,
        },
        children: []
      };
      //将转化完成的实体集添加进禁忌表，防止循环转化
      navigatorMap1.set(set.name, InjectedType.EntitySet);
      //找到set对应的type
      let type = schema.entityTypes.find(entityType => entityType.name == set.entityType);
      //将type转化为TreeNode
      this.ConvertEntityType(type, schema, navigatorMap1, customSchemaName, i18nTypes).children.forEach(child => { treeNode.children.push(child); });
      return treeNode;
    }
    //若set为空或未定义
    else return null;
  }
  //枚举转化
  public static ConvertEntityEnum(enum1: EntityType, schema: Schema, navigatorMap1: any, customSchemaName: any, i18nTypes: Map<string, string>): TreeNode {
    if (enum1) {
      let treeNode: TreeNode = {
        data: {
          id: this.oldNumber++,
          code: enum1.name,
          name: enum1.description,
          type: {
            code: Type.EntityEnum,
            description: this.getTypeDescription(Type.EntityEnum, i18nTypes)
          },
          schema: customSchemaName,
        },
        children: []
      };
      //enum1添加进禁忌表
      navigatorMap1.set(enum1.name + enum1.id, InjectedType.EntityType);
      //pro转化
      if (enum1.properties != null && enum1.properties.length > 0) {
        enum1.properties.forEach(pro => {
          let proNode: TreeNode = {
            data: {
              id: this.oldNumber++,
              code: pro.name,
              name: pro.description,
              type: {
                code: "",
                description: ""
              },
              schema: customSchemaName,
            },
            children: []
          };
          //根据枚举值的类型转换
          let dataTypeLowerCase = pro.type.toLowerCase();
          if (dataTypeLowerCase == "string") {
            proNode.data.type.code = Type.EntityEnum_String;
            proNode.data.type.description = this.getTypeDescription(Type.EntityEnum_String, i18nTypes);
          } else if (dataTypeLowerCase == "int" || dataTypeLowerCase == "integer" || dataTypeLowerCase == "byte" || dataTypeLowerCase == "decimal") {
            proNode.data.type.code = Type.EntityEnum_Int;
            proNode.data.type.description = this.getTypeDescription(Type.EntityEnum_Int, i18nTypes);
          }
          proNode.parent = treeNode;
          treeNode.children.push(proNode);
        });
      }
      return treeNode;
    }
    //若enum1为空或未定义
    else return null;
  }

}


